import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';

import { environment } from '@env';

import {
  AcquisitionChannel,
  AcquisitionChannelsResponse,
  CreateAcquisitionChannelPayload,
  PatchAcquisitionChannelPayload,
} from '../../../interfaces/acquisition.interface';
import { AuditComment, AuditCommentsResponse } from '../../../interfaces/audit-comment.interface';
import {
  BlockedEmail,
  BlockedEmailsResponse,
  CreateBlockedEmailPayload,
  GetBlockedEmailsParams,
  PatchBlockedEmailPayload,
} from '../../../interfaces/blocked-email.interface';
import {
  AddLeadAcquisitionCostPayload,
  CreateLeadPayload,
  GetLeadsParams,
  Lead,
  LeadAcquisitionCost,
  LeadAcquisitionCosts,
  LeadCaptureSettings,
  LeadCostType,
  LeadCostTypesResponse,
  LeadDuplicateDecision,
  LeadDuplicatesSettings,
  LeadOptions,
  LeadsResponse,
  PatchLeadCostTypePayload,
  PatchLeadPayload,
} from '../../../interfaces/lead.interface';
import { EntityTag } from '../../../interfaces/tag.interface';
import { LeadsApiService } from './leads-api.service';

/** Cliente de la API de leads. Los filtros viajan como `filter[x]`, los multivalor unidos por coma. */
@Injectable()
export class LeadsHttpService extends LeadsApiService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.salesApiGateway}leads`;

  list(filters: GetLeadsParams): Observable<LeadsResponse> {
    const { sort, page, perPage, ...rest } = filters;
    let params = new HttpParams();

    for (const [key, value] of Object.entries(rest)) {
      const text = Array.isArray(value) ? value.join(',') : value;
      if (text !== undefined && text !== null && text !== '') {
        params = params.set(`filter[${key}]`, String(text));
      }
    }
    if (sort) {
      params = params.set('sort', sort);
    }
    if (page) {
      params = params.set('page', page);
    }
    if (perPage) {
      params = params.set('perPage', perPage);
    }
    return this.http.get<LeadsResponse>(this.baseUrl, { params });
  }

  get(id: number): Observable<Lead> {
    return this.http.get<Lead>(`${this.baseUrl}/${id}`);
  }

  create(body: CreateLeadPayload): Observable<Lead> {
    return this.http.post<Lead>(this.baseUrl, body);
  }

  patch(id: number, body: PatchLeadPayload): Observable<Lead> {
    return this.http.patch<Lead>(`${this.baseUrl}/${id}`, body);
  }

  setTags(id: number, tagIds: number[]): Observable<{ tags: EntityTag[] }> {
    return this.http.put<{ tags: EntityTag[] }>(`${this.baseUrl}/${id}/tags`, { tag_ids: tagIds });
  }

  options(): Observable<LeadOptions> {
    return this.http.get<LeadOptions>(`${this.baseUrl}/options`);
  }

  getCaptureSettings(): Observable<LeadCaptureSettings> {
    return this.http.get<LeadCaptureSettings>(`${this.baseUrl}/settings/capture`);
  }

  updateCaptureSettings(body: LeadCaptureSettings): Observable<LeadCaptureSettings> {
    return this.http.put<LeadCaptureSettings>(`${this.baseUrl}/settings/capture`, body);
  }

  decideDuplicate(
    leadId: number,
    candidateId: number,
    decision: LeadDuplicateDecision,
  ): Observable<Lead> {
    return this.http.post<Lead>(`${this.baseUrl}/${leadId}/duplicates/${candidateId}`, {
      decision,
    });
  }

  getDuplicatesSettings(): Observable<LeadDuplicatesSettings> {
    return this.http.get<LeadDuplicatesSettings>(`${this.baseUrl}/settings/duplicates`);
  }

  updateDuplicatesSettings(body: LeadDuplicatesSettings): Observable<LeadDuplicatesSettings> {
    return this.http.put<LeadDuplicatesSettings>(`${this.baseUrl}/settings/duplicates`, body);
  }

  listBlockedEmails(filters: GetBlockedEmailsParams): Observable<BlockedEmailsResponse> {
    let params = new HttpParams();
    for (const [key, value] of Object.entries(filters)) {
      if (value !== undefined && value !== '') {
        params = params.set(`filter[${key}]`, String(value));
      }
    }
    return this.http.get<BlockedEmailsResponse>(`${this.baseUrl}/blocked-emails`, { params });
  }

  createBlockedEmail(body: CreateBlockedEmailPayload): Observable<BlockedEmail> {
    return this.http.post<BlockedEmail>(`${this.baseUrl}/blocked-emails`, body);
  }

  updateBlockedEmail(id: number, body: PatchBlockedEmailPayload): Observable<BlockedEmail> {
    return this.http.patch<BlockedEmail>(`${this.baseUrl}/blocked-emails/${id}`, body);
  }

  listComments(id: number, before?: number): Observable<AuditCommentsResponse> {
    return this.http.get<AuditCommentsResponse>(`${this.baseUrl}/${id}/comments`, {
      params: before ? { before } : {},
    });
  }

  addComment(id: number, comment: string): Observable<AuditComment> {
    return this.http.post<AuditComment>(`${this.baseUrl}/${id}/comments`, { comment });
  }

  getAcquisitionCosts(id: number): Observable<LeadAcquisitionCosts> {
    return this.http.get<LeadAcquisitionCosts>(`${this.baseUrl}/${id}/acquisition-costs`);
  }

  addAcquisitionCost(
    id: number,
    body: AddLeadAcquisitionCostPayload,
  ): Observable<LeadAcquisitionCost> {
    return this.http.post<LeadAcquisitionCost>(`${this.baseUrl}/${id}/acquisition-costs`, body);
  }

  reverseAcquisitionCost(
    id: number,
    costId: number,
    notes: string,
  ): Observable<LeadAcquisitionCost> {
    return this.http.post<LeadAcquisitionCost>(
      `${this.baseUrl}/${id}/acquisition-costs/${costId}/reversal`,
      { notes },
    );
  }

  listCostTypes(): Observable<LeadCostTypesResponse> {
    return this.http.get<LeadCostTypesResponse>(`${this.baseUrl}/cost-types`);
  }

  updateCostType(code: string, body: PatchLeadCostTypePayload): Observable<LeadCostType> {
    return this.http.patch<LeadCostType>(`${this.baseUrl}/cost-types/${code}`, body);
  }

  listChannels(): Observable<AcquisitionChannelsResponse> {
    return this.http.get<AcquisitionChannelsResponse>(`${this.baseUrl}/channels`);
  }

  createChannel(body: CreateAcquisitionChannelPayload): Observable<AcquisitionChannel> {
    return this.http.post<AcquisitionChannel>(`${this.baseUrl}/channels`, body);
  }

  updateChannel(
    code: string,
    body: PatchAcquisitionChannelPayload,
  ): Observable<AcquisitionChannel> {
    return this.http.patch<AcquisitionChannel>(`${this.baseUrl}/channels/${code}`, body);
  }
}
